import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import {
  isDocumentPreviewEnabled,
  isWorkflowStepFile,
  isWorkflowVisualizationData,
  SidebarDocument,
  SidebarDocumentPanel,
} from '@asset-metrix/portal-ui';

import { portalGatewayClient } from '../../../api/portalGatewayClient';
import type { PageSidebarPreviewedDocument } from '../../page-sidebar/types';

import { downloadSidebarDocument } from './downloadSidebarDocument';
import {
  deduplicateDocumentsById,
  getFileDownloadColumnDocuments,
  resolveSidebarDocumentSelection,
} from './sidebarDocumentUtils';
import { TableSidebarLoaderProps } from './useTableSidebarProperties';

export type DocumentSidebarLoader = Pick<
  TableSidebarLoaderProps,
  'data' | 'showWorkflowFiles' | 'fileDownloadColumnKeys'
> & {
  onSelectedDocumentChange?: (
    document: PageSidebarPreviewedDocument | null
  ) => void;
  widgetId: string;
};

export const DocviewSidebarLoader = ({
  data,
  onSelectedDocumentChange,
  showWorkflowFiles,
  widgetId,
  fileDownloadColumnKeys,
}: DocumentSidebarLoader) => {
  const documents = useMemo(() => {
    // row document
    const documents: SidebarDocument[] = [
      {
        id: String(data?.data.id),
        fileName: String(data?.data.file_name),
        mimetype:
          typeof data?.data.mimetype === 'string' ? data?.data.mimetype : '',
        ...(typeof data?.data.virus_free === 'boolean' && {
          virusFree: data.data.virus_free,
        }),
      },
    ];

    // workflow files
    const workflowVisualizationData = data?.data.workflowVisualization;
    if (
      showWorkflowFiles &&
      isWorkflowVisualizationData(workflowVisualizationData)
    ) {
      documents.push(
        ...workflowVisualizationData.steps.flatMap((step) => step.files ?? [])
      );
    }

    documents.push(
      ...getFileDownloadColumnDocuments(data?.data, fileDownloadColumnKeys)
    );

    return deduplicateDocumentsById(documents);
  }, [data?.data, fileDownloadColumnKeys, showWorkflowFiles]);

  const [selectedDocument, setSelectedDocument] = useState<
    SidebarDocument | undefined
  >(() => resolveSidebarDocumentSelection(documents, undefined));

  const [selectedDocumentPreviewUrl, setSelectedDocumentPreviewUrl] = useState<
    string | undefined
  >(undefined);
  const previewedDocument = useMemo(
    () =>
      selectedDocument
        ? { id: selectedDocument.id, label: selectedDocument.fileName }
        : null,
    [selectedDocument]
  );

  const fetchAndSetDocumentPreviewUrl = useCallback(
    async (document: SidebarDocument) => {
      try {
        setSelectedDocumentPreviewUrl(undefined);

        if (!isDocumentPreviewEnabled(document)) {
          return;
        }

        const { data } = await portalGatewayClient.table.getDocumentDownloadUrl(
          document.id,
          widgetId,
          isWorkflowStepFile(document)
            ? {
                token: document.downloadToken,
                parentDocumentId: document.parentId,
              }
            : undefined
        );

        if (data?.status === 'success' && data.data.url) {
          setSelectedDocument((current) => {
            if (current?.id === document?.id) {
              setSelectedDocumentPreviewUrl(data.data.url);
            }
            return current;
          });
        }
      } catch (e: unknown) {
        console.error(e);
      }
    },
    [widgetId]
  );

  useEffect(() => {
    setSelectedDocument(undefined);
    setSelectedDocumentPreviewUrl(undefined);
  }, [data?.rowId, widgetId]);

  useEffect(() => {
    setSelectedDocument((current) =>
      resolveSidebarDocumentSelection(documents, current)
    );
  }, [documents]);

  useEffect(() => {
    if (selectedDocument == null) {
      setSelectedDocumentPreviewUrl(undefined);
      return;
    }

    fetchAndSetDocumentPreviewUrl(selectedDocument);
  }, [fetchAndSetDocumentPreviewUrl, selectedDocument]);

  useLayoutEffect(() => {
    onSelectedDocumentChange?.(previewedDocument);
  }, [onSelectedDocumentChange, previewedDocument]);

  const onDocumentDownload = () => {
    downloadSidebarDocument(selectedDocument, {
      query: {
        inline: false,
        ...(selectedDocument != null &&
          isWorkflowStepFile(selectedDocument) && {
            token: selectedDocument.downloadToken,
            parentDocumentId: selectedDocument.parentId,
          }),
      },
      widgetId,
    });
  };

  const onDocumentClick = async (document: SidebarDocument) => {
    if (document.id === selectedDocument?.id && selectedDocumentPreviewUrl) {
      return;
    }

    setSelectedDocumentPreviewUrl(undefined);
    setSelectedDocument(document);
  };

  return (
    <SidebarDocumentPanel
      documents={documents}
      onDocumentClick={
        showWorkflowFiles || fileDownloadColumnKeys.length > 0
          ? onDocumentClick
          : undefined
      }
      onDocumentDownload={onDocumentDownload}
      selectedDocument={selectedDocument}
      selectedDocumentPreviewUrl={selectedDocumentPreviewUrl}
    />
  );
};
